import type {
  ClientRow,
  FieldDef,
  AssetRow,
  AssetFullDetail,
  RelationRow,
  Identity,
  ChangeRequestForMark,
  ChangeRequestFromMe,
  CrStatus,
} from "./types";

const LS_API = "bgspim_api_base";
const LS_JWT = "bgspim_jwt";

export function getJwt(): string | null {
  return typeof localStorage !== "undefined" ? localStorage.getItem(LS_JWT) : null;
}
export function setJwt(jwt: string): void {
  localStorage.setItem(LS_JWT, jwt);
}
export function clearJwt(): void {
  localStorage.removeItem(LS_JWT);
}

function authHeaders(): Record<string, string> {
  const jwt = getJwt();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export function apiBase(): string {
  const env = (import.meta as ImportMeta & { env?: { VITE_SPIM_API_BASE?: string } })
    .env?.VITE_SPIM_API_BASE;
  if (env) return env.replace(/\/$/, "");
  if (typeof localStorage !== "undefined") {
    const s = localStorage.getItem(LS_API);
    if (s) return s.replace(/\/$/, "");
  }
  return "https://bgspim-api.mark-8ca.workers.dev";
}

export function setApiBase(v: string): void {
  localStorage.setItem(LS_API, v.replace(/\/$/, ""));
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const base = apiBase();
  if (!base) throw new Error("API base URL not set");
  const r = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...authHeaders(),
      ...(init?.headers as Record<string, string>),
      ...(init?.body && typeof init.body === "string"
        ? { "Content-Type": "application/json" }
        : {}),
    },
  });
  const text = await r.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!r.ok) {
    const msg =
      data && typeof data === "object" && "error" in (data as object)
        ? String((data as { error: string }).error)
        : r.statusText;
    throw new Error(msg || `HTTP ${r.status}`);
  }
  return data as T;
}

export const api = {
  getClients(): Promise<{ clients: ClientRow[] }> {
    return request("/clients");
  },

  getFieldDefs(clientId: string): Promise<{ field_defs: FieldDef[] }> {
    return request(`/clients/${clientId}/field-defs`);
  },

  getAssets(
    clientId: string,
    opts?: { include_deleted?: boolean }
  ): Promise<{ assets: AssetRow[] }> {
    const qs = opts?.include_deleted
      ? "?join_fields=1&include_deleted=1"
      : "?join_fields=1";
    return request(`/clients/${clientId}/assets${qs}`);
  },

  getAsset(assetId: string): Promise<AssetFullDetail> {
    return request(`/assets/${assetId}?include=full`);
  },

  createAsset(
    clientId: string,
    body: { title: string; slug: string | null; fields: Record<string, string> }
  ): Promise<{ asset: AssetRow }> {
    return request(`/clients/${clientId}/assets`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  updateAsset(
    assetId: string,
    body: { fields: Record<string, string> }
  ): Promise<{ ok: boolean; asset_id: string; fields: Record<string, string> }> {
    return request(`/assets/${assetId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  publishAsset(assetId: string): Promise<unknown> {
    return request(`/assets/${assetId}/publish`, { method: "POST" });
  },

  deleteAsset(assetId: string): Promise<void> {
    return request(`/assets/${assetId}`, { method: "DELETE" });
  },

  // Tags
  createTag(assetId: string, tag: string): Promise<{ asset_id: string; tag: string }> {
    return request(`/assets/${assetId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag }),
    });
  },
  deleteTag(assetId: string, tag: string): Promise<void> {
    return request(`/assets/${assetId}/tags/${encodeURIComponent(tag)}`, { method: "DELETE" });
  },

  // Claim refs
  createClaimRef(assetId: string, claim_key: string): Promise<{ asset_id: string; claim_key: string }> {
    return request(`/assets/${assetId}/claim-refs`, {
      method: "POST",
      body: JSON.stringify({ claim_key }),
    });
  },
  deleteClaimRef(assetId: string, claimKey: string): Promise<void> {
    return request(`/assets/${assetId}/claim-refs/${encodeURIComponent(claimKey)}`, {
      method: "DELETE",
    });
  },

  // Specs
  createSpec(
    assetId: string,
    body: { label: string; value?: string; sort_order?: number; spec_group?: string | null }
  ): Promise<{ id: string; asset_id: string }> {
    return request(`/assets/${assetId}/specs`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  updateSpec(
    assetId: string,
    specId: string,
    body: Partial<{ label: string; value: string; sort_order: number; spec_group: string | null }>
  ): Promise<{ ok: boolean; id: string; changed: number }> {
    return request(`/assets/${assetId}/specs/${specId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  deleteSpec(assetId: string, specId: string): Promise<void> {
    return request(`/assets/${assetId}/specs/${specId}`, { method: "DELETE" });
  },

  // Relations
  getRelations(
    assetId: string,
    direction: "out" | "in" | "both" = "out"
  ): Promise<{ relations: RelationRow[] }> {
    return request(`/assets/${assetId}/relations?direction=${direction}`);
  },
  createRelation(
    assetId: string,
    body: { target_asset_id: string; relation_type: string; sort_order?: number }
  ): Promise<{ source_asset_id: string; target_asset_id: string; relation_type: string }> {
    return request(`/assets/${assetId}/relations`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  deleteRelation(assetId: string, targetId: string, relationType: string): Promise<void> {
    return request(
      `/assets/${assetId}/relations/${encodeURIComponent(targetId)}/${encodeURIComponent(relationType)}`,
      { method: "DELETE" }
    );
  },

  // Media
  createMedia(
    assetId: string,
    body: {
      image_url: string;
      cf_id?: string;
      alt_text?: string;
      is_primary?: number;
      role?: string;
      sort_order?: number;
      alt_status?: string;
      aspect?: string;
      caption?: string;
    }
  ): Promise<{ id: string; asset_id: string }> {
    return request(`/assets/${assetId}/media`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  updateMedia(
    assetId: string,
    mediaId: string,
    body: Record<string, unknown>
  ): Promise<{ ok: boolean; id: string; changed: number }> {
    return request(`/assets/${assetId}/media/${mediaId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  deleteMedia(assetId: string, mediaId: string): Promise<void> {
    return request(`/assets/${assetId}/media/${mediaId}`, { method: "DELETE" });
  },

  // Auth
  requestMagicLink(email: string): Promise<{ ok: true }> {
    return request("/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
  verifyToken(token: string): Promise<{ ok: true; jwt: string; identity: Identity }> {
    return request(`/auth/verify?token=${encodeURIComponent(token)}`);
  },
  getMe(): Promise<{ identity: Identity }> {
    return request("/me");
  },

  // Change requests
  submitFieldChangeRequest(
    assetId: string,
    body: { field_name: string; new_value: string; note?: string }
  ): Promise<{ id: string; status: CrStatus; submitted_at: string }> {
    return request(`/assets/${assetId}/change-requests`, {
      method: "POST",
      body: JSON.stringify({ op_type: "field_update", ...body }),
    });
  },

  submitImageChangeRequest(
    assetId: string,
    file: File,
    body: { image_slot: string; note?: string }
  ): Promise<{ id: string; status: CrStatus; submitted_at: string }> {
    const fd = new FormData();
    fd.append("op_type", "image_update");
    fd.append("image_slot", body.image_slot);
    if (body.note) fd.append("note", body.note);
    fd.append("image", file);
    return request(`/assets/${assetId}/change-requests`, {
      method: "POST",
      body: fd,
    });
  },

  listChangeRequests(opts?: {
    status?: CrStatus | "all";
    client_id?: string;
  }): Promise<{ change_requests: ChangeRequestForMark[] }> {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", opts.status);
    if (opts?.client_id) params.set("client_id", opts.client_id);
    const qs = params.toString();
    return request(`/change-requests${qs ? `?${qs}` : ""}`);
  },

  markChangeRequestDone(
    crId: string
  ): Promise<{ id: string; status: "done"; done_at: string }> {
    return request(`/change-requests/${crId}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "done" }),
    });
  },

  deleteChangeRequest(crId: string): Promise<void> {
    return request(`/change-requests/${crId}`, { method: "DELETE" });
  },

  myChangeRequests(opts?: {
    asset_id?: string;
    status?: CrStatus | "all";
  }): Promise<{ change_requests: ChangeRequestFromMe[] }> {
    const params = new URLSearchParams();
    if (opts?.asset_id) params.set("asset_id", opts.asset_id);
    if (opts?.status) params.set("status", opts.status);
    const qs = params.toString();
    return request(`/me/change-requests${qs ? `?${qs}` : ""}`);
  },

  // Field defs
  updateFieldDef(
    fieldDefId: string,
    body: Partial<{
      field_label: string;
      field_type: string;
      display_order: number;
      required: number;
      options: string;
      placeholder: string;
      show_in_list: number;
      show_in_detail: number;
      active: number;
    }>
  ): Promise<{ ok: boolean; id: string; changed: number }> {
    return request(`/field-defs/${fieldDefId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
};
